import * as React from "react";
import {
  autoUpdate,
  flip,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useMergeRefs,
} from "@floating-ui/react";
import { CheckboxIcon, useIcon } from "@salt-ds/core";
import { StatusAdornment } from "../display/StatusAdornment";

import { usePortalScopeClassName } from "../floating/usePortalScope";
import { mergeProps } from "../utils/mergeProps";
import { useFormFieldProps } from "../salt/form-field-context";
import {
  ListControlContext,
  useListControl,
  useListControlContext,
  type ListControl,
  type OptionValue,
} from "./useListControl";
import "./dropdown.css";

/**
 * Dropdown, Option, OptionGroup and OptionList, replacing `@salt-ds/core`'s behind
 * `dropdown-field.tsx` — the most-used remaining component, in 102 files.
 *
 * ── IT IS A <button role="combobox">, NOT A <select> ──────────────────────────────────
 *
 * A native `<select>` cannot be styled beyond a few properties, cannot show a checkbox against
 * each option, and renders its list in the OS rather than the page. The combobox pattern is what
 * buys those, and the price is that every keyboard behaviour a select gives free has to be
 * written: typeahead, Home/End, PageUp/PageDown, and the rule that Enter picks the ACTIVE option
 * rather than the focused element.
 *
 * `aria-activedescendant` is the mechanism: focus never leaves the button, and the button names
 * the option currently highlighted. That is why options carry `tabIndex={-1}` and why clicking
 * one pulls focus back to the list rather than keeping it.
 *
 * ── THE LIST IS MEASURED, NOT GUESSED ─────────────────────────────────────────────────
 *
 * `--overlay-minWidth` is set from the trigger's width so the list is never narrower than the
 * control it belongs to, and `--overlay-maxHeight` from the space actually available — floored
 * at five rows, because a list clamped to two rows near the bottom of a window is unusable and
 * scrolling the page to fix it closes the list.
 */
/*
 * Read from the vendor's `.d.ts`. Two things were missing on the first attempt and both are live
 * call sites in `dropdown-field.tsx`: a "tertiary" variant, and `OverlayProps` — the escape hatch
 * for putting attributes on the LIST rather than the trigger. Sixth time in this migration that
 * the declaration carried more than the implementation showed.
 */
export type DropdownVariant = "primary" | "secondary" | "tertiary";
export type DropdownValidationStatus = "error" | "warning" | "success";

function ExpandToggle({ open }: { open: boolean }) {
  // Through the semantic icon registry, so an icon-set swap reaches this too.
  const { CollapseIcon, ExpandIcon } = useIcon();
  const Icon = open ? CollapseIcon : ExpandIcon;
  return <Icon aria-hidden className="saltDropdown-toggle" />;
}

export interface DropdownProps<Item = string>
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onSelect" | "value"> {
  multiselect?: boolean;
  selected?: Item[];
  defaultSelected?: Item[];
  onSelectionChange?: (event: React.SyntheticEvent, selected: Item[]) => void;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  placeholder?: string;
  value?: string;
  readOnly?: boolean;
  emptyReadOnlyMarker?: string;
  startAdornment?: React.ReactNode;
  variant?: DropdownVariant;
  validationStatus?: DropdownValidationStatus;
  bordered?: boolean;
  /** Mirrors the vendor: the FormField's necessity wins when there is one. */
  required?: boolean;
  /** Attributes for the option LIST, which is a different element from the trigger. */
  OverlayProps?: Omit<React.ComponentPropsWithoutRef<"div">, "children" | "id">;
  valueToString?: (item: Item) => string;
}

export const Dropdown = React.forwardRef<HTMLButtonElement, DropdownProps>(function Dropdown(
  {
    "aria-describedby": ariaDescribedBy,
    "aria-labelledby": ariaLabelledBy,
    bordered = false,
    children,
    className,
    defaultOpen,
    defaultSelected,
    disabled: disabledProp,
    emptyReadOnlyMarker = "—",
    multiselect,
    onBlur,
    onFocus,
    onKeyDown,
    onOpenChange,
    onSelectionChange,
    open,
    OverlayProps,
    placeholder,
    readOnly: readOnlyProp,
    required: requiredProp,
    selected,
    startAdornment,
    validationStatus: validationStatusProp,
    value,
    valueToString = (item: string) => String(item),
    variant = "primary",
    ...rest
  },
  ref,
) {
  const {
    a11yProps: {
      "aria-describedby": formFieldDescribedBy,
      "aria-labelledby": formFieldLabelledBy,
    } = {},
    disabled: formFieldDisabled,
    readOnly: formFieldReadOnly,
    necessity: formFieldRequired,
    validationStatus: formFieldValidationStatus,
  } = useFormFieldProps();

  const disabled = Boolean(disabledProp) || formFieldDisabled;
  const readOnly = Boolean(readOnlyProp) || formFieldReadOnly;
  const validationStatus = validationStatusProp ?? formFieldValidationStatus;
  const required = formFieldRequired
    ? ["required", "asterisk"].includes(formFieldRequired)
    : requiredProp;

  const listControl = useListControl<string>({
    open,
    defaultOpen,
    onOpenChange,
    multiselect,
    defaultSelected,
    selected,
    onSelectionChange,
    disabled,
    readOnly,
    valueToString,
  });

  const {
    activeState,
    focusedState,
    getFirstOption,
    getIndexOfOption,
    getLastOption,
    getOptionAfter,
    getOptionBefore,
    getOptionFromSearch,
    getOptionPageAbove,
    getOptionPageBelow,
    getOptionsMatching,
    listRef,
    openKey,
    openState,
    select,
    selectedState,
    setActive,
    setFocusVisibleState,
    setFocusedState,
    setListRef,
    setOpen,
  } = listControl;

  const selectedValue = selectedState.map((item) => valueToString(item)).join(", ");
  const isEmptyReadOnly = readOnly && selectedValue === "";
  const valueText = isEmptyReadOnly ? emptyReadOnlyMarker : (value ?? selectedValue);

  const childCount = React.Children.count(children);
  const [referenceEl, setReferenceEl] = React.useState<Element | null>(null);

  const { context, elements, floatingStyles, refs } = useFloating({
    open: openState && !readOnly && childCount > 0,
    onOpenChange: (nextOpen, _event, reason) => {
      // Focus alone must not open the list: a Tab through a form would open every dropdown it
      // passed, and the one that stayed open would swallow the next keystroke.
      const focusNotBlur = reason === "focus" && nextOpen;
      if (readOnly || focusNotBlur) return;
      setOpen(nextOpen);
    },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(1),
      size({
        apply({
          rects,
          elements: sized,
          availableHeight,
        }: {
          rects: { reference: { width: number } };
          elements: { floating: HTMLElement };
          availableHeight: number;
        }) {
          // Never narrower than its trigger.
          sized.floating.style.setProperty("--overlay-minWidth", `${rects.reference.width}px`);
          // Floored at five rows — a list clamped to two near the bottom of a window is unusable,
          // and scrolling the page to fix it closes the list.
          sized.floating.style.setProperty(
            "--overlay-maxHeight",
            `max(calc((var(--salt-size-base) + var(--salt-spacing-100)) * 5), calc(${availableHeight}px - var(--salt-spacing-100)))`,
          );
        },
      }),
      // `initialPlacement` so a list that has flipped up does not flip back mid-scroll.
      flip({ fallbackStrategy: "initialPlacement" }),
    ],
  });

  const { getFloatingProps, getReferenceProps } = useInteractions([
    useDismiss(context),
    useFocus(context),
    useClick(context),
  ]);

  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const mergedButtonRef = useMergeRefs([
    buttonRef,
    ref,
    (node: HTMLButtonElement | null) => {
      setReferenceEl(node);
      refs.setReference(node);
    },
  ]);
  const mergedListRef = useMergeRefs([setListRef, refs.setFloating]);
  const portalScope = usePortalScopeClassName(referenceEl);

  const typeaheadString = React.useRef("");
  const typeaheadTimeout = React.useRef<number | undefined>(undefined);

  const handleTypeahead = (event: React.KeyboardEvent) => {
    if (typeaheadTimeout.current) clearTimeout(typeaheadTimeout.current);
    typeaheadString.current += event.key;
    // 500ms is the window in which successive keys form one search. Longer and a pause between
    // words joins two searches; shorter and ordinary typing never matches a two-letter prefix.
    typeaheadTimeout.current = window.setTimeout(() => {
      typeaheadString.current = "";
    }, 500);

    if (!openState) setOpen(true, "input");

    // Search from the active option first so repeated letters cycle forward; fall back to a
    // search from the top so the last option does not become a dead end.
    /*
     * `getOptionFromSearch` returns the OPTION VALUE, while the navigation helpers
     * (`getOptionAfter` and friends) return the registration record with a `.data`. Conflating
     * the two is easy and silent — `setActive(record)` stores an object with no `.value`, so the
     * highlight lands nowhere and Enter selects nothing.
     */
    const newOption: OptionValue<string> | undefined =
      getOptionFromSearch(typeaheadString.current, activeState) ??
      getOptionFromSearch(typeaheadString.current);
    if (newOption) {
      setActive(newOption);
      setFocusVisibleState(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    onKeyDown?.(event);
    if (readOnly) return;

    if (!openState && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      // The key is remembered so the list opens with the first or last option active.
      setOpen(true, undefined, event.key);
      return;
    }

    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      event.stopPropagation();
      handleTypeahead(event);
    }

    const activeOption = activeState ?? getFirstOption()?.data;
    if (activeOption === undefined) return;

    let newActive;
    switch (event.key) {
      case "ArrowDown":
        newActive = getOptionAfter(activeOption) ?? getLastOption();
        break;
      case "ArrowUp":
        newActive = getOptionBefore(activeOption) ?? getFirstOption();
        break;
      case "Home":
        newActive = getFirstOption();
        break;
      case "End":
        newActive = getLastOption();
        break;
      case "PageUp":
        newActive = getOptionPageAbove(activeOption);
        break;
      case "PageDown":
        newActive = getOptionPageBelow(activeOption);
        break;
      case "Enter":
      case " ":
        // A space that is part of a search is typing, not selection — otherwise searching for
        // "New York" picks whatever was active the moment the space was typed.
        if (
          (openState && Boolean(activeState?.disabled)) ||
          (typeaheadString.current.trim().length > 0 && event.key === " ")
        ) {
          event.preventDefault();
          return;
        }
        if (!openState || !activeState) return;
        event.preventDefault();
        select(event, activeState);
        break;
      case "Tab":
        // Tabbing away commits the highlighted option in a single-select list, which is what a
        // native select does. Multi-select does not, because there is no single obvious choice.
        if (!multiselect && activeState) select(event, activeState);
        break;
    }

    if (newActive && newActive.data.id !== activeState?.id) {
      event.preventDefault();
      setActive(newActive.data);
      setFocusVisibleState(true);
    }
  };

  React.useEffect(() => {
    if (!openState) {
      setActive(undefined);
      return;
    }
    const activeIndex = activeState ? getIndexOfOption(activeState) : -1;
    if (activeIndex > 0) return;

    let newActive;
    // Opening on a selection starts there, so reopening a list does not lose the user's place.
    if (selectedState.length > 0) {
      newActive = getOptionsMatching((option) => option.value === selectedState[0]).pop();
    }
    if (!newActive) {
      if (openKey.current === "ArrowDown") newActive = getFirstOption();
      else if (openKey.current === "ArrowUp") newActive = getLastOption();
    }
    if (!newActive) newActive = getFirstOption();
    setActive(newActive?.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openState, children]);

  const listId = React.useId();
  const listOpen = (openState || focusedState) && !readOnly && childCount > 0;

  const classes = ["saltDropdown", `saltDropdown-${variant}`];
  if (disabled) classes.push("saltDropdown-disabled");
  if (validationStatus) classes.push(`saltDropdown-${validationStatus}`);
  if (bordered) classes.push("saltDropdown-bordered");
  if (className) classes.push(className);

  return (
    <ListControlContext.Provider value={listControl as unknown as ListControl<never>}>
      <button
        aria-activedescendant={activeState?.id}
        aria-controls={openState ? listId : undefined}
        aria-describedby={[formFieldDescribedBy, ariaDescribedBy].filter(Boolean).join(" ") || undefined}
        aria-expanded={openState}
        aria-labelledby={[formFieldLabelledBy, ariaLabelledBy].filter(Boolean).join(" ") || undefined}
        aria-readonly={readOnly ? "true" : undefined}
        aria-required={required ? "true" : undefined}
        className={classes.join(" ")}
        disabled={disabled}
        ref={mergedButtonRef}
        role="combobox"
        type="button"
        {...getReferenceProps({
          onKeyDown: handleKeyDown,
          onFocus: (event: React.FocusEvent<HTMLButtonElement>) => {
            setFocusedState(true);
            onFocus?.(event);
          },
          onBlur: (event: React.FocusEvent<HTMLButtonElement>) => {
            setFocusedState(false);
            // Not a real blur if focus moved INTO the list — that is still this control.
            if (!listRef.current || !listRef.current.contains(event.relatedTarget)) {
              onBlur?.(event);
            }
          },
          ...rest,
        })}
      >
        {startAdornment ? (
          <div className="saltDropdown-startAdornmentContainer">{startAdornment}</div>
        ) : null}
        <span
          className={
            valueText ? "saltDropdown-content" : "saltDropdown-content saltDropdown-placeholder"
          }
        >
          {!valueText ? placeholder : valueText}
        </span>
        {!disabled && validationStatus ? <StatusAdornment status={validationStatus} /> : null}
        {!readOnly ? <ExpandToggle open={openState} /> : null}
        <div className="saltDropdown-activationIndicator" />
      </button>
      {listOpen ? (
        <div className={portalScope}>
          <div
            aria-multiselectable={multiselect}
            id={listId}
            role="listbox"
            /*
             * MERGED, not spread. `OverlayProps` is the caller's escape hatch onto the list, and
             * a plain spread lets its `className` REPLACE `saltOptionList` — the list keeps the
             * consumer's class and loses every rule that makes it a list.
             * `dropdown-field.test.tsx` asserts both are present, which is how this was caught.
             */
            {...mergeProps(
              {
                className: openState
                  ? "saltOptionList"
                  : "saltOptionList saltOptionList-collapsed",
              },
              getFloatingProps({
                // Moving the pointer over the list turns OFF the keyboard highlight, so a mouse
                // user is not followed around by a stray active row.
                onMouseOver: () => setFocusVisibleState(false),
                // Focus and clicks inside the list go back to the button, because
                // `aria-activedescendant` requires focus to stay on the combobox.
                onFocus: () => buttonRef.current?.focus(),
                onClick: () => buttonRef.current?.focus(),
                ...OverlayProps,
              }) as Record<string, unknown>,
            )}
            ref={mergedListRef}
            style={floatingStyles}
          >
            <div className="saltOptionListBase">{children}</div>
          </div>
        </div>
      ) : (
        // Rendered but empty keeps `elements.floating` stable for the measurement middleware.
        <span hidden ref={() => undefined} />
      )}
    </ListControlContext.Provider>
  );
}) as <Item = string>(
  props: DropdownProps<Item> & { ref?: React.Ref<HTMLButtonElement> },
) => React.ReactElement;

export interface OptionProps<Item = string> extends React.HTMLAttributes<HTMLDivElement> {
  value: Item;
  disabled?: boolean;
}

export const Option = React.forwardRef<HTMLDivElement, OptionProps>(function Option(
  { children, className, disabled: disabledProp, id: idProp, onClick, value, ...rest },
  ref,
) {
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  const optionRef = React.useRef<HTMLDivElement | null>(null);
  const mergedRef = useMergeRefs([optionRef, ref]);

  const {
    activeState,
    disabled: listDisabled,
    focusVisibleState,
    listRef,
    multiselect,
    register,
    select,
    selectedState,
    setActive,
    valueToString,
  } = useListControlContext<string>();

  const disabled = disabledProp || listDisabled;
  const selected = selectedState.includes(value);
  const active = activeState?.value === value;

  const optionValue = React.useMemo(
    () => ({ id: String(id), disabled: Boolean(disabled), value }),
    [id, disabled, value],
  );

  React.useEffect(() => {
    if (id && optionRef.current) return register(optionValue, optionRef.current);
  }, [optionValue, id, register]);

  const classes = ["saltOption"];
  if (active) classes.push("saltOption-active");
  // The highlight appears only when the user is navigating by keyboard.
  if (focusVisibleState && active) classes.push("saltOption-focusVisible");
  if (className) classes.push(className);

  return (
    <div
      aria-disabled={disabled ? "true" : undefined}
      aria-selected={selected}
      className={classes.join(" ")}
      id={id}
      onClick={(event) => {
        if (disabled || id === undefined) return;
        // `preventScroll` so returning focus does not jump the list back to the top.
        listRef?.current?.focus({ preventScroll: true });
        setActive(optionValue);
        select(event, optionValue);
        onClick?.(event);
      }}
      onMouseOver={() => setActive(optionValue)}
      ref={mergedRef}
      role="option"
      // Focus never lands here — the combobox owns it and names this option through
      // `aria-activedescendant`.
      tabIndex={-1}
      {...rest}
    >
      {multiselect ? <CheckboxIcon checked={selected} /> : null}
      {children ?? valueToString(value)}
    </div>
  );
});

export interface OptionGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: React.ReactNode;
}

export const OptionGroup = React.forwardRef<HTMLDivElement, OptionGroupProps>(
  function OptionGroup({ children, className, label, ...rest }, ref) {
    const labelId = React.useId();
    return (
      <div
        aria-labelledby={label ? labelId : undefined}
        className={className ? `saltOptionGroup ${className}` : "saltOptionGroup"}
        ref={ref}
        role="group"
        {...rest}
      >
        {/* `aria-hidden` because the group is already NAMED by this label — leaving it readable
            announces the heading twice, once as the name and again as an item. */}
        {label ? (
          <div aria-hidden className="saltOptionGroup-label" id={labelId}>
            {label}
          </div>
        ) : null}
        {children}
      </div>
    );
  },
);
